import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  Box,
  Text,
  InputProps,
  useOutsideClick,
  InputRightElement,
  InputGroup,
  Spinner,
  InputLeftElement,
} from "@chakra-ui/react";

interface KYDSearchInputProps
  extends Omit<InputProps, "onChange" | "onSelect"> {
  items: Array<{ id: string; [key: string]: any }>;
  onSelect?: (item: { id: string; [key: string]: any }) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchField?: string;
  displayField?: string;
  maxHeight?: string;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  itemComponent?: (
    item: { id: string; [key: string]: any },
    index: number
  ) => React.ReactNode;
}

const KYDSearchInput: React.FC<KYDSearchInputProps> = ({
  items,
  onSelect,
  onChange,
  placeholder = "Search...",
  searchField = "email",
  displayField = "email",
  maxHeight = "200px",
  loading = false,
  leftIcon,
  itemComponent,
  ...props
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filteredItems, setFilteredItems] = useState<
    Array<{ id: string; [key: string]: any }>
  >([]);
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  useEffect(() => {
    if (!searchQuery && !searchField) return;
    if (searchQuery.trim() === "") {
      setFilteredItems(items);
    } else {
      const filtered = items.filter((item) =>
        item[searchField]?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, items, searchField]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (onChange) {
      onChange(value);
    }
    setIsOpen(true);
  };

  const handleItemClick = (item: { id: string; [key: string]: any }) => {
    onSelect(item);
    setIsOpen(false);
  };

  const renderItem = (
    item: { id: string; [key: string]: any },
    index: number
  ) => {
    if (itemComponent) {
      return (
        <Box
          key={item.id}
          as="button"
          onClick={() => handleItemClick(item)}
          w="100%"
          bg="transparent"
        >
          {itemComponent(item, index)}
        </Box>
      );
    }
    return (
      <Box
        key={item.id}
        p="10px"
        cursor="pointer"
        _hover={{ bg: "#F7FAFC" }}
        onClick={() => handleItemClick(item)}
      >
        <Text fontSize="14px" fontFamily="Helvetica">
          {item[displayField]}
        </Text>
      </Box>
    );
  };
  return (
    <Box
      position="relative"
      width={props.width}
      ref={ref}
      boxShadow="0px 0px 10px 0px #0000000D"
      border="1px solid #E6E6E6"
      borderRadius="8px"
      overflow="visible"
    >
      <InputGroup>
        <Input
          placeholder={placeholder}
          border="none"
          height="44px"
          width="100%"
          _placeholder={{ color: "#00000080" }}
          fontSize="14px"
          fontWeight="500"
          value={searchQuery}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
          {...props}
        />
        {leftIcon && <InputLeftElement>{leftIcon}</InputLeftElement>}
        {loading && (
          <InputRightElement>{<Spinner color="gray.500" />}</InputRightElement>
        )}
      </InputGroup>
      {isOpen && filteredItems.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          width="100%"
          maxHeight={maxHeight}
          overflowY="auto"
          border="1px solid #E2E8F0"
          borderRadius="8px"
          bg="white"
          zIndex="9999"
          mt="2px"
          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
        >
          {filteredItems.map((item, index) => renderItem(item, index))}
        </Box>
      )}
    </Box>
  );
};

export default KYDSearchInput;
